import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Search, BookOpen, Clock, Users, Star, CheckCircle, PlayCircle, Award } from '../../components/CustomIcons';

const CATEGORIES = ['All', 'Technology', 'Business', 'Healthcare', 'Logistics', 'Academic'];

const CATEGORY_COLORS = {
  Technology: '#3B82F6',
  Business: '#8B5CF6',
  Healthcare: '#10B981',
  Logistics: '#F59E0B',
  Academic: '#F97316',
};

const STATUS_STYLES = {
  in_progress: { bg: 'rgba(59,130,246,0.12)', color: '#3B82F6', label: 'In Progress' },
  complete: { bg: 'rgba(34,197,94,0.12)', color: '#22C55E', label: 'Completed' },
  pending: { bg: 'rgba(245,158,11,0.12)', color: '#F59E0B', label: 'Pending Approval' },
  approved: { bg: 'rgba(34,197,94,0.12)', color: '#22C55E', label: 'Approved' },
};

function CourseCard({ course, onEnroll, enrollmentStatus, enrollmentId, onUpdateProgress }) {
  const [loading, setLoading] = useState(false);
  const color = CATEGORY_COLORS[course.category] || '#F97316';
  const enrolled = !!enrollmentStatus;
  const st = STATUS_STYLES[enrollmentStatus] || null;

  const handleEnroll = async () => {
    setLoading(true);
    await onEnroll(course.id);
    setLoading(false);
  };

  const handleComplete = async () => {
    if (!enrollmentId) return;
    setLoading(true);
    await onUpdateProgress(enrollmentId, 100);
    setLoading(false);
  };

  return (
    <div className="card" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px', transition: 'all 0.25s ease', position: 'relative' }}
      data-testid="course-card"
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.35)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = ''; }}>

      {/* Status badge */}
      {st && (
        <div style={{ position: 'absolute', top: '14px', right: '14px', fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px', background: st.bg, color: st.color, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          {st.label}
        </div>
      )}

      {/* Provider + Category */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: enrolled ? '80px' : '0' }}>
        <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'Inter, sans-serif' }}>
          {course.provider_name}
        </span>
        <span style={{ fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px', background: `${color}18`, color: color, border: `1px solid ${color}33`, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          {course.category}
        </span>
      </div>

      {/* Title */}
      <h3 style={{ margin: 0, fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)', lineHeight: 1.35 }}>
        {course.title}
      </h3>

      {/* Description */}
      <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)', fontFamily: 'Inter, sans-serif', lineHeight: 1.55, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
        {course.description}
      </p>

      {/* Tags */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
        {(course.tags || []).slice(0, 3).map(tag => (
          <span key={tag} className="badge-grey">{tag}</span>
        ))}
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: '14px', borderTop: '1px solid var(--border-subtle)', paddingTop: '12px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <Clock size={13} style={{ color: 'var(--text-muted)' }} />
          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{course.duration_hours}h</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <Users size={13} style={{ color: 'var(--text-muted)' }} />
          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{course.enrolled_count} enrolled</span>
        </div>
        {course.rating > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <Star size={13} style={{ color: 'var(--orange)' }} fill="var(--orange)" />
            <span style={{ fontSize: '12px', color: 'var(--orange)', fontWeight: 600 }}>{course.rating}</span>
          </div>
        )}
        <div style={{ marginLeft: 'auto' }}>
          {course.price_credits === 0
            ? <span style={{ fontSize: '12px', color: 'var(--success)', fontWeight: 700 }}>Free</span>
            : <span style={{ fontSize: '12px', color: 'var(--orange)', fontWeight: 700 }}>{course.price_credits?.toLocaleString()} credits</span>
          }
        </div>
      </div>

      {/* CTA */}
      {enrollmentStatus === 'complete' ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', borderRadius: '10px', background: 'rgba(34,197,94,0.08)', border: '1px solid #22C55E', color: '#22C55E', fontSize: '13px', fontWeight: 600, fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          data-testid="completed-badge">
          <Award size={15} />
          Completed
        </div>
      ) : enrollmentStatus === 'in_progress' ? (
        <button className="btn-primary" onClick={handleComplete} disabled={loading} style={{ justifyContent: 'center', background: 'rgba(59,130,246,0.12)', color: '#3B82F6', border: '1px solid rgba(59,130,246,0.3)' }}
          data-testid={`mark-complete-${course.id}`}>
          <PlayCircle size={15} />
          {loading ? 'Updating...' : 'Mark Complete'}
        </button>
      ) : enrollmentStatus === 'pending' ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', borderRadius: '10px', background: 'var(--warning-bg)', border: '1px solid var(--warning)', color: 'var(--warning)', fontSize: '13px', fontWeight: 600, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          <CheckCircle size={15} />
          Access Requested
        </div>
      ) : (
        <button className="btn-primary" onClick={handleEnroll} disabled={loading} style={{ justifyContent: 'center' }}
          data-testid={`enroll-${course.id}`}>
          {loading ? 'Enrolling...' : course.price_credits === 0 ? 'Enroll Free' : 'Request Access'}
        </button>
      )}
    </div>
  );
}

function MyCourseCard({ enrollment }) {
  const course = enrollment.course || {};
  const st = STATUS_STYLES[enrollment.status] || STATUS_STYLES.in_progress;
  const progress = enrollment.progress_percent || 0;
  const color = CATEGORY_COLORS[course.category] || '#F97316';

  return (
    <div className="card" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}
      data-testid="my-course-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px', background: `${color}18`, color: color, border: `1px solid ${color}33` }}>
              {course.category}
            </span>
            <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px', background: st.bg, color: st.color }}>
              {st.label}
            </span>
          </div>
          <h3 style={{ margin: 0, fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)', lineHeight: 1.3 }}>
            {course.title || 'Course'}
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--text-muted)' }}>
            {course.provider_name} · {course.duration_hours}h
          </p>
        </div>
        {enrollment.status === 'complete' && (
          <Award size={28} color="#22C55E" />
        )}
      </div>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Progress</span>
          <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--orange)' }}>{progress}%</span>
        </div>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%`, background: enrollment.status === 'complete' ? '#22C55E' : 'var(--orange)' }} />
        </div>
      </div>
      <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-dim)' }}>
        Enrolled {new Date(enrollment.created_at).toLocaleDateString()}
        {enrollment.completed_at && ` · Completed ${new Date(enrollment.completed_at).toLocaleDateString()}`}
      </p>
    </div>
  );
}

export default function MarketplaceCatalog() {
  const { authFetch, user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [enrollments, setEnrollments] = useState({});
  const [myEnrollments, setMyEnrollments] = useState([]);
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState('All');
  const [activeTab, setActiveTab] = useState('catalog');

  useEffect(() => {
    Promise.all([
      authFetch('/marketplace/catalog'),
      authFetch('/marketplace/providers'),
      user?.role === 'learner' ? authFetch('/marketplace/my-enrollments') : Promise.resolve({ data: [] }),
    ]).then(([coursesRes, providersRes, enrollRes]) => {
      setCourses(coursesRes.data || []);
      setProviders(providersRes.data || []);
      const myEnrollData = enrollRes.data || [];
      setMyEnrollments(myEnrollData);
      const statusMap = {};
      const idMap = {};
      myEnrollData.forEach(e => {
        statusMap[e.course_id] = e.status;
        idMap[e.course_id] = e.id;
      });
      setEnrollments({ statusMap, idMap });
    }).catch(e => console.error('catalog-load error:', e)).finally(() => setLoading(false));
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEnroll = async (courseId) => {
    try {
      const res = await authFetch(`/marketplace/courses/${courseId}/enroll`, { method: 'POST' });
      const data = res.data || {};
      setEnrollments(prev => ({
        statusMap: { ...prev.statusMap, [courseId]: data.status || 'in_progress' },
        idMap: { ...prev.idMap, [courseId]: data.enrollment_id },
      }));
      // Add to myEnrollments
      const course = courses.find(c => c.id === courseId);
      if (course) {
        setMyEnrollments(prev => [{
          id: data.enrollment_id,
          course_id: courseId,
          status: data.status || 'in_progress',
          progress_percent: 0,
          created_at: new Date().toISOString(),
          course: { ...course, provider_name: course.provider_name },
        }, ...prev]);
      }
    } catch (e) { console.error('enroll error:', e); }
  };

  const handleUpdateProgress = async (enrollmentId, progressPercent) => {
    try {
      await authFetch(`/marketplace/enrollments/${enrollmentId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ progress_percent: progressPercent }),
      });
      setMyEnrollments(prev => prev.map(e =>
        e.id === enrollmentId
          ? { ...e, progress_percent: progressPercent, status: progressPercent === 100 ? 'complete' : e.status, completed_at: progressPercent === 100 ? new Date().toISOString() : e.completed_at }
          : e
      ));
      // Update status map
      if (progressPercent === 100) {
        setEnrollments(prev => {
          const enrollment = myEnrollments.find(e => e.id === enrollmentId);
          if (!enrollment) return prev;
          return { ...prev, statusMap: { ...prev.statusMap, [enrollment.course_id]: 'complete' } };
        });
      }
    } catch (e) { console.error('progress-update error:', e); }
  };

  const filtered = courses.filter(c => {
    const matchSearch = !search || c.title.toLowerCase().includes(search.toLowerCase()) || c.description.toLowerCase().includes(search.toLowerCase());
    const matchCat = category === 'All' || c.category === category;
    const matchProvider = selectedProvider === 'All' || c.provider_id === selectedProvider;
    return matchSearch && matchCat && matchProvider;
  });

  const completedCount = myEnrollments.filter(e => e.status === 'complete').length;
  const inProgressCount = myEnrollments.filter(e => e.status === 'in_progress').length;

  return (
    <div className="page-wrapper" style={{ padding: '24px', background: 'var(--bg-page)', minHeight: '100vh' }} data-testid="marketplace-catalog">
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <div className="overline" style={{ marginBottom: '6px' }}>Module 11</div>
          <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '28px', color: 'var(--text-primary)', margin: '0 0 6px 0' }}>
            Assistiv Marketplace
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', fontFamily: 'Inter, sans-serif', margin: 0 }}>
            Expert-led courses from verified partner organizations — all built for your future.
          </p>
        </div>

        {/* Stats Bar */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
          {[
            { label: 'Partner Providers', value: providers.length },
            { label: 'Available Courses', value: courses.length },
            { label: 'My Enrollments', value: myEnrollments.length },
            { label: 'Completed', value: completedCount },
          ].map(s => (
            <div key={s.label} className="card" style={{ padding: '12px 18px', display: 'flex', gap: '10px', alignItems: 'center', flex: '0 0 auto' }}>
              <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '20px', color: 'var(--orange)' }}>{s.value}</span>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{s.label}</span>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', background: 'var(--bg-elevated)', borderRadius: '12px', padding: '4px', width: 'fit-content' }}>
          {[
            { id: 'catalog', label: `All Courses (${courses.length})` },
            { id: 'my-courses', label: `My Courses (${myEnrollments.length})` },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              style={{ padding: '8px 20px', borderRadius: '9px', border: 'none', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '13px', transition: 'all 0.18s ease', background: activeTab === tab.id ? 'var(--bg-surface)' : 'transparent', color: activeTab === tab.id ? 'var(--text-primary)' : 'var(--text-muted)', boxShadow: activeTab === tab.id ? '0 2px 8px rgba(0,0,0,0.2)' : 'none' }}
              data-testid={`tab-${tab.id}`}>
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'my-courses' ? (
          /* ── My Courses tab ── */
          <div>
            {myEnrollments.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <BookOpen size={40} style={{ color: 'var(--text-muted)', marginBottom: '12px' }} />
                <p style={{ color: 'var(--text-muted)', fontSize: '15px' }}>No enrollments yet — browse the catalog to get started.</p>
                <button className="btn-primary" style={{ marginTop: '16px' }} onClick={() => setActiveTab('catalog')}>
                  Browse Catalog
                </button>
              </div>
            ) : (
              <>
                {inProgressCount > 0 && (
                  <div style={{ marginBottom: '24px' }}>
                    <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '16px', color: 'var(--text-primary)', margin: '0 0 12px 0' }}>
                      In Progress ({inProgressCount})
                    </h2>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                      {myEnrollments.filter(e => e.status === 'in_progress').map(e => (
                        <MyCourseCard key={e.id} enrollment={e} />
                      ))}
                    </div>
                  </div>
                )}
                {completedCount > 0 && (
                  <div>
                    <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '16px', color: 'var(--text-primary)', margin: '0 0 12px 0' }}>
                      Completed ({completedCount})
                    </h2>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                      {myEnrollments.filter(e => e.status === 'complete').map(e => (
                        <MyCourseCard key={e.id} enrollment={e} />
                      ))}
                    </div>
                  </div>
                )}
                {myEnrollments.filter(e => e.status === 'pending').length > 0 && (
                  <div style={{ marginTop: completedCount > 0 || inProgressCount > 0 ? '24px' : '0' }}>
                    <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '16px', color: 'var(--text-primary)', margin: '0 0 12px 0' }}>
                      Pending Approval ({myEnrollments.filter(e => e.status === 'pending').length})
                    </h2>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                      {myEnrollments.filter(e => e.status === 'pending').map(e => (
                        <MyCourseCard key={e.id} enrollment={e} />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          /* ── Catalog tab ── */
          <>
            {/* Filters */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
                <Search size={15} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input className="input-dark" value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search courses..." style={{ paddingLeft: '38px' }} data-testid="catalog-search" />
              </div>
              <select value={category} onChange={e => setCategory(e.target.value)} className="input-dark" style={{ width: 'auto', minWidth: '130px' }}
                data-testid="catalog-category-filter">
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
              <select value={selectedProvider} onChange={e => setSelectedProvider(e.target.value)} className="input-dark" style={{ width: 'auto', minWidth: '150px' }}
                data-testid="catalog-provider-filter">
                <option value="All">All Providers</option>
                {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>

            {/* Category Pills */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
              {CATEGORIES.map(c => (
                <button key={c} onClick={() => setCategory(c)}
                  style={{ padding: '7px 16px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '13px', background: category === c ? 'var(--orange)' : 'var(--bg-elevated)', color: category === c ? '#111111' : 'var(--text-secondary)', transition: 'all 0.18s ease' }}
                  data-testid={`category-pill-${c.toLowerCase()}`}>
                  {c}
                </button>
              ))}
            </div>

            {/* Grid */}
            {loading ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                {[...Array(6)].map((_, i) => <div key={i} className="skeleton" style={{ height: '280px', borderRadius: '14px' }} />)}
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <BookOpen size={40} style={{ color: 'var(--text-muted)', marginBottom: '12px' }} />
                <p style={{ color: 'var(--text-muted)', fontSize: '15px' }}>No courses match your filters.</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                {filtered.map(course => (
                  <CourseCard
                    key={course.id}
                    course={course}
                    onEnroll={handleEnroll}
                    enrollmentStatus={enrollments.statusMap?.[course.id]}
                    enrollmentId={enrollments.idMap?.[course.id]}
                    onUpdateProgress={handleUpdateProgress}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

